/**
 * AgentPlaybookPage — Landlord Portal
 *
 * Route: /settings/agents/:agentKind/playbook
 *
 * The landlord's playbook for one agent: the merge-order statement, the "Talk to the agent" card,
 * Always applies (read-only), the All properties group (rules and defaults) and, by property, one
 * collapsible group per property with its own rules. Edits are local (usePlaybookDraft, one draft
 * per rule scope) until that group's "Save changes"; in-app navigation with unsaved edits in any
 * group opens the unsaved-changes dialog (useUnsavedChangesGuard).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { playbookAgentKindSchema, type AgentPlaybookKindView, type PlaybookAgentKind } from '@resida/api-types/agent';
import {
  PLAYBOOK_PROPERTIES_QUERY_KEY,
  getAgentPlaybooks,
  listPlaybookProperties,
  saveAgentPlaybook,
  PlaybookValidationError,
} from '../api/agent-playbooks.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { PlaybookLockedList } from '../components/settings/playbook/PlaybookLockedList';
import { PlaybookRulesEditor } from '../components/settings/playbook/PlaybookRulesEditor';
import { PlaybookDefaultsList } from '../components/settings/playbook/PlaybookDefaultsList';
import { PlaybookSaveBar } from '../components/settings/playbook/PlaybookSaveBar';
import {
  PlaybookCounts,
  PlaybookPropertyGroup,
  type PlaybookGroupSaveState,
} from '../components/settings/playbook/PlaybookPropertyGroup';
import { useImprovementConversationOpener } from '../components/agent/improvement/ImprovementConversationProvider';
import { AGENT_PLAYBOOKS_QUERY_KEY } from '../components/agent/AgentSettingsLink';
import {
  LANDLORD_SCOPE,
  usePlaybookDraft,
  type NewPropertyScope,
  type PlaybookScopeKey,
} from '../hooks/usePlaybookDraft';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { AGENT_KIND_NOUN, agentName, countDefaults, mapPlaybookErrors, type PlaybookErrorMap } from '../lib/playbook';

const NO_ERRORS: PlaybookErrorMap = { rules: {}, defaults: {}, general: [] };

export function AgentPlaybookPage() {
  const { agentKind } = useParams<{ agentKind: string }>();
  const parsedKind = playbookAgentKindSchema.safeParse(agentKind);
  if (!parsedKind.success) return <Navigate to="/settings/agents" replace />;
  return <AgentPlaybookLoader key={parsedKind.data} agentKind={parsedKind.data} />;
}

function AgentPlaybookLoader({ agentKind }: { agentKind: PlaybookAgentKind }) {
  const [view, setView] = useState<AgentPlaybookKindView | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      setLoading(true);
      setFailed(false);
      try {
        const data = await getAgentPlaybooks();
        if (isCancelled()) return;
        const found = data.playbooks.find((p) => p.agentKind === agentKind);
        if (found) setView(found);
        else setFailed(true);
      } catch {
        if (isCancelled()) return;
        setFailed(true);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [agentKind],
  );

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  return (
    <SettingsLayout hideMobileTabs>
      <div className="px-[var(--page-padding-x)]" data-testid="agent-playbook-page">
        <div className="mb-6 flex flex-col gap-2">
          <Link
            to="/settings/agents"
            data-testid="playbook-back-link"
            className="inline-flex min-h-[44px] items-center gap-1 self-start text-sm font-semibold text-primary hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            AI agents
          </Link>
          <h1 className="text-xl font-bold text-foreground">{agentName(agentKind)} playbook</h1>
        </div>

        {loading && (
          <div className="flex flex-col gap-4" data-testid="agent-playbook-loading" aria-busy="true">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {!loading && failed && (
          <div
            role="alert"
            data-testid="agent-playbooks-error"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
          >
            <span>
              <span className="font-semibold text-destructive">We couldn't load your agents' playbooks.</span> Your
              agents keep working from the version you last saved.
            </span>
            <Button type="button" variant="outline" onClick={() => void load()} data-testid="agent-playbooks-retry">
              Retry
            </Button>
          </div>
        )}

        {!loading && !failed && view && (
          <PlaybookEditor agentKind={agentKind} view={view} onSaved={setView} />
        )}
      </div>
    </SettingsLayout>
  );
}

interface PlaybookEditorProps {
  agentKind: PlaybookAgentKind;
  view: AgentPlaybookKindView;
  onSaved: (view: AgentPlaybookKindView) => void;
}

const SAVED_MESSAGE = (noun: string) => `Saved — your ${noun} agent uses this playbook from its next step.`;

interface ScopeSaveUi {
  saving: boolean;
  saveError: string | null;
  errors: PlaybookErrorMap;
  statusMessage: string | null;
}

const CLEAN_UI: ScopeSaveUi = { saving: false, saveError: null, errors: NO_ERRORS, statusMessage: null };

type PlaybookScope = ReturnType<ReturnType<typeof usePlaybookDraft>['scope']>;

/**
 * Saving one rule scope: its own request, its own saving flag, validation errors and status
 * (BR-SETTINGS-AGENTS-SAVE). `registerSaver` lets the page's unsaved-changes guard save every
 * dirty scope.
 */
function useScopeSave(
  agentKind: PlaybookAgentKind,
  scopeKey: PlaybookScopeKey,
  scope: PlaybookScope,
  onSaved: (view: AgentPlaybookKindView) => void,
  registerSaver: (scopeKey: PlaybookScopeKey, save: (() => Promise<boolean>) | null) => void,
) {
  const queryClient = useQueryClient();
  const [ui, setUi] = useState<ScopeSaveUi>(CLEAN_UI);
  const noun = AGENT_KIND_NOUN[agentKind];
  const { changeCount } = scope;

  const save = async (): Promise<boolean> => {
    setUi({ ...CLEAN_UI, saving: true });
    try {
      const saved = await saveAgentPlaybook(agentKind, scope.toSaveBody());
      onSaved(saved);
      void queryClient.invalidateQueries({ queryKey: AGENT_PLAYBOOKS_QUERY_KEY });
      setUi({ ...CLEAN_UI, statusMessage: SAVED_MESSAGE(noun) });
      return true;
    } catch (err) {
      if (err instanceof PlaybookValidationError) {
        setUi({ ...CLEAN_UI, errors: mapPlaybookErrors(err.body.errors, scope.draft) });
      } else {
        setUi({
          ...CLEAN_UI,
          saveError: "We couldn't save your playbook. Nothing was changed — your edits are still here.",
        });
      }
      return false;
    }
  };

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });
  useEffect(() => {
    registerSaver(scopeKey, () => saveRef.current());
    return () => registerSaver(scopeKey, null);
  }, [registerSaver, scopeKey]);

  // A stale row outline must not outlive the edit that fixes it.
  useEffect(() => {
    setUi((prev) => (prev.errors === NO_ERRORS ? prev : { ...prev, errors: NO_ERRORS }));
  }, [changeCount]);

  const state: PlaybookGroupSaveState = {
    ...ui,
    onSave: () => void save(),
    onDiscard: () => {
      setUi(CLEAN_UI);
      scope.discard();
    },
  };
  return state;
}

interface PropertyGroupContainerProps {
  agentKind: PlaybookAgentKind;
  view: AgentPlaybookKindView;
  playbook: ReturnType<typeof usePlaybookDraft>;
  propertyId: string;
  propertyName: string;
  defaultExpanded: boolean;
  onSaved: (view: AgentPlaybookKindView) => void;
  registerSaver: (scopeKey: PlaybookScopeKey, save: (() => Promise<boolean>) | null) => void;
}

function PropertyGroupContainer({
  agentKind,
  view,
  playbook,
  propertyId,
  propertyName,
  defaultExpanded,
  onSaved,
  registerSaver,
}: PropertyGroupContainerProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const scope = playbook.scope(propertyId);
  const save = useScopeSave(agentKind, propertyId, scope, onSaved, registerSaver);
  const saved = view.propertyScopes.find((p) => p.propertyId === propertyId);
  return (
    <PlaybookPropertyGroup
      agentKind={agentKind}
      propertyId={propertyId}
      propertyName={propertyName}
      counts={saved?.counts ?? { rules: 0, defaultsOff: 0, defaultsReworded: 0 }}
      expanded={expanded}
      onToggle={() => setExpanded((e) => !e)}
      scope={scope}
      catalogDefaults={view.items.filter((i) => i.tier === 'default')}
      inheritedFrom={view.defaults}
      save={save}
    />
  );
}

function PlaybookEditor({ agentKind, view, onSaved }: PlaybookEditorProps) {
  const noun = AGENT_KIND_NOUN[agentKind];
  const openConversation = useImprovementConversationOpener();
  const [addedAll, setAdded] = useState<NewPropertyScope[]>([]);
  // A property whose first save landed is now in the read; only the not-yet-created ones stay "added".
  const added = useMemo(
    () => addedAll.filter((a) => !view.propertyScopes.some((p) => p.propertyId === a.propertyId)),
    [addedAll, view.propertyScopes],
  );
  const playbook = usePlaybookDraft(view, added);
  const landlord = playbook.scope(LANDLORD_SCOPE);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Defaults and Always applies stay folded away until asked for (design brief, screen 3), but a
  // default with a validation error opens them so the outlined row is never hidden.
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  const savers = useRef(new Map<PlaybookScopeKey, () => Promise<boolean>>());
  const registerSaver = useCallback((scopeKey: PlaybookScopeKey, save: (() => Promise<boolean>) | null) => {
    if (save) savers.current.set(scopeKey, save);
    else savers.current.delete(scopeKey);
  }, []);
  const playbookRef = useRef(playbook);
  useEffect(() => {
    playbookRef.current = playbook;
  });

  const landlordSave = useScopeSave(agentKind, LANDLORD_SCOPE, landlord, onSaved, registerSaver);
  const hasDefaultErrors = Object.keys(landlordSave.errors.defaults).length > 0;

  // Save every group with unsaved edits (one request per group); true only if all of them saved.
  const [savingAll, setSavingAll] = useState(false);
  const saveAllDirty = async (): Promise<boolean> => {
    let allSaved = true;
    setSavingAll(true);
    try {
      for (const [scopeKey, save] of savers.current) {
        if (!playbookRef.current.scope(scopeKey).isDirty) continue;
        if (!(await save())) allSaved = false;
      }
    } finally {
      setSavingAll(false);
    }
    return allSaved;
  };

  const { dialog } = useUnsavedChangesGuard({
    when: playbook.anyDirty,
    onSave: saveAllDirty,
    message: `Save your changes to the ${noun} agent playbook before you leave? If you discard them, your agent keeps working from the playbook you last saved.`,
  });

  const { data: activeProperties } = useQuery({
    queryKey: PLAYBOOK_PROPERTIES_QUERY_KEY,
    queryFn: listPlaybookProperties,
    staleTime: 60_000,
    retry: false,
  });
  // Property groups in property-name order (locale compare): saved documents, then groups just added.
  const groups = [
    ...view.propertyScopes.map((p) => ({ propertyId: p.propertyId, propertyName: p.propertyName, isNew: false })),
    ...added.map((a) => ({ propertyId: a.propertyId, propertyName: a.propertyName, isNew: true })),
  ].sort((a, b) => a.propertyName.localeCompare(b.propertyName));
  const pickable = (activeProperties ?? []).filter((p) => !groups.some((g) => g.propertyId === p.id));

  const catalogDefaults = view.items.filter((i) => i.tier === 'default');
  const lockedItems = view.items.filter((i) => i.tier === 'locked').map((i) => i.title);
  const landlordCounts = { rules: view.rules.length, ...countDefaults(view.defaults) };

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-2xl text-sm text-muted-foreground" data-testid="playbook-order-statement">
        What takes priority, highest first: <strong className="font-semibold">Always applies</strong>, then rules for a
        property, then rules for all properties, then defaults. Items marked Always applies can't be changed.
      </p>

      <div
        className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4"
        data-testid="playbook-talk-card"
      >
        <div className="flex min-w-[200px] flex-1 flex-col gap-0.5">
          <span className="text-[15px] font-semibold text-foreground">Rather just say what you want?</span>
          <span className="text-[13px] text-muted-foreground">Describe it and the agent drafts the rule for you to confirm.</span>
        </div>
        <Button
          type="button"
          className="font-semibold max-sm:w-full"
          onClick={() =>
            openConversation({
              start: { entry: 'rules_page', anchor: { kind: 'none', agentKind } },
              agentKind,
              subtitle: `About how your ${noun} agent works`,
            })
          }
          data-testid="playbook-talk-to-agent"
        >
          Talk to the agent
        </Button>
      </div>

      <section
        role="group"
        aria-labelledby="playbook-all-properties-heading"
        className="flex flex-col gap-6 rounded-xl border border-border bg-card p-4 md:p-6"
        data-testid="playbook-all-properties"
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 id="playbook-all-properties-heading" className="m-0 flex-1 text-lg font-semibold text-foreground">
            All properties
          </h2>
          <PlaybookCounts counts={landlordCounts} testIdPrefix="playbook-all-properties" />
        </div>
        <PlaybookRulesEditor
          agentKind={agentKind}
          rules={landlord.draft.rules}
          errors={landlordSave.errors.rules}
          listError={landlordSave.errors.general.join(' ') || undefined}
          onAdd={landlord.addRule}
          onEdit={landlord.editRule}
          onRemove={landlord.removeRule}
          onMoveUp={landlord.moveRuleUp}
        />
        <details
          open={defaultsOpen || hasDefaultErrors}
          onToggle={(e) => setDefaultsOpen((e.target as HTMLDetailsElement).open)}
          className="rounded-lg border border-border"
          data-testid="playbook-defaults-disclosure"
        >
          <summary
            className="flex min-h-[44px] cursor-pointer items-center px-3 text-sm font-semibold text-foreground"
            data-testid="playbook-defaults-disclosure-toggle"
          >
            Defaults ({catalogDefaults.length}) and what always applies ({lockedItems.length})
          </summary>
          <div className="flex flex-col gap-6 px-3 pb-4 pt-2">
            <PlaybookDefaultsList
              catalogDefaults={catalogDefaults}
              customizations={landlord.draft.defaults}
              errors={landlordSave.errors.defaults}
              onToggle={landlord.toggleDefault}
              onReword={landlord.rewordDefault}
              onReset={landlord.resetDefault}
              onRemoveRetired={landlord.removeRetired}
            />
            <PlaybookLockedList items={lockedItems} />
          </div>
        </details>
      </section>

      {landlordSave.saveError && (
        <div
          role="alert"
          data-testid="playbook-save-error"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
        >
          <span className="text-destructive">{landlordSave.saveError}</span>
          <Button type="button" variant="outline" onClick={landlordSave.onSave} data-testid="playbook-save-retry">
            Retry
          </Button>
        </div>
      )}

      <section
        aria-labelledby="playbook-by-property-heading"
        className="flex flex-col gap-3"
        data-testid="playbook-by-property-section"
      >
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="playbook-by-property-heading" className="m-0 flex-1 text-lg font-semibold text-foreground">
            By property
          </h2>
          <Button
            type="button"
            variant="outline"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((o) => !o)}
            data-testid="playbook-add-property-button"
          >
            Add property rules
          </Button>
        </div>

        {pickerOpen && (
          <div
            role="group"
            aria-label="Choose a property"
            className="flex flex-col gap-1 rounded-xl border border-border bg-card p-2"
            data-testid="playbook-add-property-picker"
          >
            {pickable.length === 0 ? (
              <p className="m-0 px-2 py-2 text-sm text-muted-foreground" data-testid="playbook-add-property-empty">
                Every property already has its own rules.
              </p>
            ) : (
              pickable.map((p) => (
                <Button
                  key={p.id}
                  type="button"
                  variant="ghost"
                  className="justify-start"
                  onClick={() => {
                    setAdded((prev) => [...prev, { propertyId: p.id, propertyName: p.name }]);
                    setPickerOpen(false);
                  }}
                  data-testid={`playbook-add-property-option-${p.id}`}
                >
                  {p.name}
                </Button>
              ))
            )}
          </div>
        )}

        {groups.map((g) => (
          <PropertyGroupContainer
            key={g.propertyId}
            agentKind={agentKind}
            view={view}
            playbook={playbook}
            propertyId={g.propertyId}
            propertyName={g.propertyName}
            defaultExpanded={g.isNew}
            onSaved={onSaved}
            registerSaver={registerSaver}
          />
        ))}
      </section>

      {/* Page-level status: dirty if ANY group is dirty. Each group keeps its own Save
          (BR-SETTINGS-AGENTS-SAVE); the bar's Save sends one POST per dirty group. */}
      <PlaybookSaveBar
        changeCount={playbook.totalChangeCount}
        saving={savingAll || landlordSave.saving}
        statusMessage={landlordSave.statusMessage}
        onSave={() => void saveAllDirty()}
        onDiscard={() => {
          playbook.discardAll();
          landlordSave.onDiscard();
        }}
      />
      {dialog}
    </div>
  );
}

export default AgentPlaybookPage;
